import { supabase } from '../supabase';

export interface ConfiguracionPermiso {
  id: number;
  clave: string;
  nombre: string;
  descripcion?: string;
  roles_permitidos: string[];
}

/**
 * Servicio para gestionar configuraciones de permisos visuales
 */
export const configuracionService = {
  /**
   * Obtener una configuración por su clave
   */
  async getConfiguracion(clave: string): Promise<ConfiguracionPermiso | null> {
    try {
      const { data, error } = await supabase
        .from('configuraciones_permisos')
        .select('*')
        .eq('clave', clave)
        .maybeSingle(); // Usar maybeSingle en lugar de single para evitar error cuando no hay datos

      // PGRST116 significa que no hay resultados, no es un error crítico
      if (error) {
        if (error.code === 'PGRST116') {
          console.log(`⚠️ No se encontró configuración con clave: ${clave} (tabla vacía o no existe)`);
        } else {
          console.error('Error obteniendo configuración:', error);
        }
        return null;
      }

      if (!data) {
        console.log(`⚠️ No se encontró configuración con clave: ${clave}`);
        return null;
      }

      return {
        ...data,
        roles_permitidos: Array.isArray(data.roles_permitidos)
          ? data.roles_permitidos
          : typeof data.roles_permitidos === 'string'
          ? JSON.parse(data.roles_permitidos)
          : [],
      };
    } catch (error) {
      console.error('Error en getConfiguracion:', error);
      return null;
    }
  },

  /**
   * Verificar si un rol puede ver una funcionalidad específica
   */
  async puedeVerFuncionalidad(clave: string, userRole: string): Promise<boolean> {
    try {
      console.log(`🔍 Verificando permiso: clave=${clave}, userRole=${userRole}`);
      
      // Owner siempre puede ver todo
      if (userRole === 'owner') {
        console.log('✅ Usuario es owner, permitir acceso');
        return true;
      }

      const config = await this.getConfiguracion(clave);
      
      if (!config) {
        console.log('⚠️ No se encontró configuración, denegar acceso por seguridad');
        // Si no existe la configuración, denegar por seguridad
        return false;
      }

      console.log('📋 Configuración encontrada:', {
        clave: config.clave,
        roles_permitidos: config.roles_permitidos,
        tipo: typeof config.roles_permitidos,
        esArray: Array.isArray(config.roles_permitidos)
      });

      // Asegurarse de que roles_permitidos es un array
      const rolesPermitidos = Array.isArray(config.roles_permitidos)
        ? config.roles_permitidos
        : typeof config.roles_permitidos === 'string'
        ? JSON.parse(config.roles_permitidos)
        : [];

      const tienePermiso = rolesPermitidos.includes(userRole);
      console.log(`🔐 Resultado verificación: ${tienePermiso ? 'PERMITIDO' : 'DENEGADO'}`);
      
      return tienePermiso;
    } catch (error) {
      console.error('❌ Error en puedeVerFuncionalidad:', error);
      // En caso de error, denegar por seguridad
      return false;
    }
  },

  /**
   * Verificar si el usuario actual puede ver el historial de cambios
   */
  async puedeVerHistorialCambios(userRole: string): Promise<boolean> {
    return await this.puedeVerFuncionalidad('ver_historial_cambios', userRole);
  },

  /**
   * Obtener una configuración general por su clave
   */
  async getConfiguracionGeneral(clave: string): Promise<{ valor: string; tipo: string } | null> {
    try {
      const { data, error } = await supabase
        .from('configuraciones_generales')
        .select('valor, tipo')
        .eq('clave', clave)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error obteniendo configuración general:', error);
        return null;
      }

      if (!data) {
        console.log(`⚠️ No se encontró configuración general con clave: ${clave}`);
        return null;
      }

      return {
        valor: data.valor,
        tipo: data.tipo,
      };
    } catch (error) {
      console.error('Error en getConfiguracionGeneral:', error);
      return null;
    }
  },

  /**
   * Obtener el límite de caracteres para la descripción de propuestas
   */
  async getLimiteDescripcionPropuestas(): Promise<number> {
    try {
      const config = await this.getConfiguracionGeneral('propuestas_limite_descripcion');
      
      if (!config) {
        // Valor por defecto si no existe la configuración
        return 240;
      }

      // Convertir el valor a número según el tipo
      if (config.tipo === 'numero') {
        const numero = parseInt(config.valor, 10);
        return isNaN(numero) ? 240 : numero;
      }

      return 240;
    } catch (error) {
      console.error('Error obteniendo límite de descripción:', error);
      return 240; // Valor por defecto
    }
  },

  /**
   * Distancia máxima (metros) para poder votar cerca de una sede.
   * Default 500 si no hay configuración.
   */
  async getRangoVotacionMetros(): Promise<number> {
    try {
      const config = await this.getConfiguracionGeneral('votacion_rango_metros');
      if (!config) return 500;
      if (config.tipo === 'numero') {
        const numero = parseInt(config.valor, 10);
        return Number.isFinite(numero) && numero > 0 ? numero : 500;
      }
      const numero = parseInt(config.valor, 10);
      return Number.isFinite(numero) && numero > 0 ? numero : 500;
    } catch (error) {
      console.error('Error obteniendo rango de votación:', error);
      return 500;
    }
  },

  /**
   * Si true, QR y /votar funcionan en navegador. Si false, en web se redirige a /app.
   * Por defecto false (solo app) si no hay fila en configuraciones_generales.
   */
  async isVotacionWebHabilitada(): Promise<boolean> {
    try {
      const config = await this.getConfiguracionGeneral('votacion_web_habilitada');
      if (!config) return false;
      const v = String(config.valor).trim().toLowerCase();
      return v === 'true' || v === '1' || v === 'si' || v === 'sí';
    } catch (error) {
      console.error('Error obteniendo votacion_web_habilitada:', error);
      return false;
    }
  },
};
